import React, { FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { DEV_TOOL_USE_BE_EXPERIENCE_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { getLocalStorageEntry } from 'src/utils/webStorageUtils';
import { useDevToolPermissions } from 'src/apps/Tools/DevTools/utils';
import { useQuery } from '@tanstack/react-query';
import { ExperienceApi } from 'src/api';
import { useUserContext } from 'src/context/user';
import { useRouter } from 'next/router';
import { WebViewClient } from 'src/context/communicate';

export type ExperienceType = 'classic' | '2024' | '2025';

export type ExperienceContextValue = {
  isLoaded: boolean;
  isError: boolean;
  experienceType: ExperienceType | null;
};

const defaultValue: ExperienceContextValue = {
  isLoaded: false,
  isError: false,
  experienceType: null,
};

export const ExperienceContext = React.createContext<{
  isLoaded: boolean;
  isError: boolean;
  experienceType: ExperienceType | null;
}>(defaultValue);

export const useExperienceContext = () => useContext(ExperienceContext);

export const ExperienceProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const areDevToolsAllowed = useDevToolPermissions();
  const { user } = useUserContext();
  const { query } = useRouter();
  const [experienceType, setExperienceType] = useState<ExperienceType | null>(
    defaultValue.experienceType,
  );

  const { data: experienceData, isError } = useQuery({
    queryKey: ['experience', user?.id],
    queryFn: () => ExperienceApi.getExperience(),
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (!experienceData) return;

    /* istanbul ignore next: temporary implementation, will be removed in WEBSITE-7254 */
    if (
      (query.client &&
        [WebViewClient.ANDROID, WebViewClient.IOS].includes(query.client as WebViewClient)) ||
      (areDevToolsAllowed &&
        getLocalStorageEntry(DEV_TOOL_USE_BE_EXPERIENCE_STORAGE_KEY) === 'true')
    ) {
      setExperienceType(experienceData.current);
      return;
    }

    setExperienceType('2024');
  }, [areDevToolsAllowed, experienceData, query.client]);

  const experience = useMemo(
    () => ({
      isLoaded: experienceType !== null,
      isError,
      experienceType,
    }),
    [experienceType, isError],
  );

  return <ExperienceContext.Provider value={experience}>{children}</ExperienceContext.Provider>;
};
